import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import type { ReviewDecision } from '@hp/domain';
import { HpTag, Kbd, PageHeading } from '@hp/ui';
import { useCan, useMe } from '../api/me';
import { invalidatePipelineState } from '../features/pipeline/queries';
import { isTrpcClientError, trpc } from '../api/trpc';
import { invalidateReview, useCluster, type ClusterDetail } from '../features/review/queries';
import { EntityBadge, StatusBadge, TierBadge } from '../components/badges';
import { ConfirmDialog } from '../components/confirm-dialog';
import { ErrorAlert } from '../components/error-alert';
import { RelativeTime } from '../components/relative-time';
import { Spinner } from '../components/ui/icons';
import { CompareTable } from '../features/review/compare-table';
import { DecisionBar } from '../features/review/decision-bar';
import { DecisionsPanel, EvidencePanel } from '../features/review/evidence-panel';
import { computeDefaultSurvivor } from '../features/review/survivor';
import { useQueueNav } from '../features/review/use-queue-nav';

function ClusterDetailView({ detail, clusterId }: { detail: ClusterDetail; clusterId: number }) {
  const { t } = useTranslation(['review', 'common']);
  const me = useMe();
  const pdDomain = me.data?.pipedriveCompanyDomain;
  const canDecide = useCan()('crm.review');
  const { cluster, members, endpoints, pairs, decisions, deleteAll } = detail;
  const isPending = cluster.status === 'pending';
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const defaultSurvivor = useMemo(() => computeDefaultSurvivor(members), [members]);
  const [survivorSel, setSurvivorSel] = useState<number | null>(null);
  const survivor = survivorSel ?? defaultSurvivor;
  const [overrides, setOverrides] = useState<Record<string, number>>({});

  const { goTo } = useQueueNav(cluster.entityType, clusterId);

  function toggleOverride(fieldKey: string, pdId: number) {
    setOverrides((prev) => {
      if (prev[fieldKey] === pdId) {
        const { [fieldKey]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [fieldKey]: pdId };
    });
  }

  const decide = useMutation(
    trpc.review.decide.mutationOptions({
      onSuccess: () => {
        void invalidateReview();
        void invalidatePipelineState();
        // advance to the next pending cluster (or back to the queue when none is left)
        goTo(1);
      },
      onError: (err) => {
        // CONFLICT: someone else decided meanwhile — refresh to show current state
        if (isTrpcClientError(err) && err.data?.code === 'CONFLICT') void invalidateReview();
      },
    }),
  );
  const submit = (body: {
    decision: ReviewDecision;
    survivorPdId?: number;
    fieldOverrides?: Record<string, number>;
    note?: string;
  }): void => decide.mutate({ clusterId, ...body });

  function approve() {
    if (!isPending || !canDecide || decide.isPending) return;
    submit({
      decision: 'approve_merge',
      survivorPdId: survivor ?? undefined,
      fieldOverrides: Object.keys(overrides).length > 0 ? overrides : undefined,
    });
  }

  function notDuplicate() {
    if (!isPending || !canDecide || decide.isPending) return;
    const note = window.prompt(t('detail.notDuplicatePrompt'), '');
    if (note === null) return; // cancelled
    submit({ decision: 'not_duplicate', note: note.trim() || undefined });
  }

  function defer() {
    if (!isPending || !canDecide || decide.isPending) return;
    submit({ decision: 'defer' });
  }

  // "Delete all" has no keyboard shortcut on purpose: it is confirmed in a modal, click only.
  function openDeleteAll() {
    if (!isPending || !canDecide || decide.isPending || !deleteAll.allowed) return;
    setDeleteDialogOpen(true);
  }

  function confirmDeleteAll() {
    setDeleteDialogOpen(false);
    submit({ decision: 'approve_delete' });
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      )
        return;
      switch (e.key) {
        case 'a':
          approve();
          break;
        case 'n':
          notDuplicate();
          break;
        case 'd':
          defer();
          break;
        // ← previous, → next: the key on the left goes back (j/k was vim's down/up, which read backwards here)
        case 'ArrowRight':
          goTo(1);
          break;
        case 'ArrowLeft':
          goTo(-1);
          break;
        default:
          return;
      }
      e.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="bo-sections">
      <PageHeading
        title={t('detail.title')}
        recordId={`#${cluster.id}`}
        meta={
          <div className="bo-row bo-job-meta">
            <EntityBadge entityType={cluster.entityType} />
            <TierBadge tier={cluster.tier} />
            <StatusBadge status={cluster.status} />
            <HpTag tone="neutral">{t('detail.records', { count: cluster.size })}</HpTag>
            <span className="bo-muted">
              {t('detail.created')} <RelativeTime value={cluster.createdAt} />
            </span>
          </div>
        }
        actions={
          <>
            <span className="bo-kbd-hints" title={t('detail.kbdTitle')}>
              <Kbd>A</Kbd> {t('detail.kbdApprove')} <Kbd>N</Kbd> {t('detail.kbdNotDup')} <Kbd>D</Kbd>{' '}
              {t('detail.kbdDefer')} <Kbd>←</Kbd>/<Kbd>→</Kbd> {t('detail.kbdPrevNext')}
            </span>
            <BackLink />
          </>
        }
      />

      <ErrorAlert error={decide.error} />

      <CompareTable
        cluster={cluster}
        members={members}
        endpoints={endpoints}
        pdDomain={pdDomain}
        survivor={survivor}
        defaultSurvivor={defaultSurvivor}
        isPending={isPending && canDecide}
        onSelectSurvivor={setSurvivorSel}
        overrides={overrides}
        onToggleOverride={toggleOverride}
      />

      <DecisionBar
        canDecide={canDecide}
        clusterStatus={cluster.status}
        clusterResolution={cluster.resolution}
        isPending={isPending}
        busy={decide.isPending ? (decide.variables?.decision ?? null) : null}
        overridesCount={Object.keys(overrides).length}
        deleteAll={deleteAll}
        onApprove={approve}
        onNotDuplicate={notDuplicate}
        onDeleteAll={openDeleteAll}
        onDefer={defer}
        onReopen={() => submit({ decision: 'reopen' })}
        onPrev={() => goTo(-1)}
        onNext={() => goTo(1)}
      />

      <div className="bo-history-grid">
        <EvidencePanel pairs={pairs} />
        <DecisionsPanel decisions={decisions} />
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={t('detail.deleteAllTitle')}
        confirmLabel={t('detail.deleteAllConfirm')}
        danger
        busy={decide.isPending}
        onConfirm={confirmDeleteAll}
        onCancel={() => setDeleteDialogOpen(false)}
      >
        <p>{t('detail.deleteAllIntro', { count: members.length })}</p>
        <ul className="bo-dialog__list">
          {members.map((m) => (
            <li key={m.pdId}>
              {t('detail.deleteAllRecord', { id: m.pdId, name: m.name ?? t('detail.deleteAllUnnamed') })}
            </li>
          ))}
        </ul>
        <p>{t('detail.deleteAllWhen')}</p>
        <p className="bo-muted">{t('detail.deleteAllRetention')}</p>
      </ConfirmDialog>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation('review');
  return (
    <Link to="/crm-data/deduplication" className="hp-detail-action hp-detail-action--text">
      {t('detail.backToQueue')}
    </Link>
  );
}

export default function ReviewDetail() {
  const { t } = useTranslation('review');
  const params = useParams();
  const id = Number(params.id);
  const detail = useCluster(id);

  if (detail.isLoading) return <Spinner>{t('detail.loading')}</Spinner>;
  if (detail.error) {
    return (
      <div className="bo-sections">
        <BackLink />
        <ErrorAlert error={detail.error} />
      </div>
    );
  }
  if (!detail.data) return null;
  // key by cluster id so survivor/override state resets when navigating j/k
  return <ClusterDetailView key={id} detail={detail.data} clusterId={id} />;
}
